
import React, { Suspense, useEffect, useRef, useState } from 'react'
import * as THREE from 'three'
import { Canvas, useLoader, useFrame, useThree } from '@react-three/fiber'
import { Stage, OrbitControls, Html } from '@react-three/drei'
import { FaPlus, FaMinus, FaArrowRotateLeft } from "react-icons/fa6";
import { useGlobalcontext } from './context'
import Length2000 from './Models/Length2000'
import Length3000 from './Models/Length3000'
import Length4000 from './Models/Length4000'
import Length5000 from './Models/Length5000'
import Length6000 from './Models/Length6000'
import Length7000 from './Models/Length7000'
import Length8000 from './Models/Length8000'
import Length9000 from './Models/Length9000'
import Length10000 from './Models/Length10000'
import Length11000 from './Models/Length11000'
import Length12000 from './Models/Length12000'
import { Menu, Separator, useContextMenu } from 'react-contexify';
import PropagateLoader from "react-spinners/PropagateLoader";
import 'react-contexify/ReactContexify.css';
import { Environment } from './Environment'
const CANVAS_ID = "canvas-context-menu";


const LoadingIndicator = () => {
  return (
    <Html center>
      <PropagateLoader
        color={"#FFFFFF"}
        loading={true}
        size={15}
        aria-label="Loading Spinner"
        data-testid="loader"
      />
    </Html>
  );
};

const ConfigurationStatusBar = () => {
  const { formData, material } = useGlobalcontext();
  return (
    <div className="configuration-status-bar" style={{
      position: 'absolute',
      bottom: '24px',
      left: '50%',
      transform: 'translateX(-50%)',
      backgroundColor: 'rgba(23, 25, 30, 0.75)',
      backdropFilter: 'blur(8px)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      borderRadius: '20px',
      padding: '8px 16px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      color: '#E2E8F0',
      fontFamily: 'Lato, sans-serif',
      fontSize: '13px',
      fontWeight: '600',
      pointerEvents: 'none',
      userSelect: 'none',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
      zIndex: 10
    }}>
      <span style={{
        width: '6px',
        height: '6px',
        borderRadius: '50%',
        backgroundColor: '#10B981',
        display: 'inline-block'
      }}></span>
      <span>{`Ø${formData.diameter}mm · L${formData.length}mm · ${material}`}</span>
    </div>
  );
};

const ViewerControls = ({ controlsRef }) => {

  const handleZoomIn = (e) => {
    e.stopPropagation();
    const controls = controlsRef.current;
    if (controls) {
      const camera = controls.object;
      const target = controls.target;
      if (camera && target) {
        camera.position.lerp(target, 0.15);
        controls.update();
      }
    }
  };

  const handleZoomOut = (e) => {
    e.stopPropagation();
    const controls = controlsRef.current;
    if (controls) {
      const camera = controls.object;
      const target = controls.target;
      if (camera && target) {
        const direction = new THREE.Vector3().subVectors(camera.position, target).normalize();
        camera.position.addScaledVector(direction, 100);
        controls.update();
      }
    }
  };

  const handleReset = (e) => {
    e.stopPropagation();
    const controls = controlsRef.current;
    if (controls) {
      controls.reset();
    }
  };



  return (
    <div className="viewer-controls" style={{
      position: 'absolute',
      bottom: '24px',
      right: '24px',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: 'rgba(23, 25, 30, 0.75)',
      backdropFilter: 'blur(8px)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      borderRadius: '20px',
      padding: '4px',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
      zIndex: 10
    }}>
      <button
        onClick={handleZoomIn}
        style={{
          width: '36px',
          height: '36px',
          border: 'none',
          background: 'none',
          color: '#E2E8F0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          borderRadius: '50%',
          transition: 'background-color 0.2s',
        }}
        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'}
        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
        title="Zoom In"
      >
        <FaPlus size={14} />
      </button>
      <button
        onClick={handleZoomOut}
        style={{
          width: '36px',
          height: '36px',
          border: 'none',
          background: 'none',
          color: '#E2E8F0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          borderRadius: '50%',
          transition: 'background-color 0.2s',
        }}
        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'}
        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
        title="Zoom Out"
      >
        <FaMinus size={14} />
      </button>
      <button
        onClick={handleReset}
        style={{
          width: '36px',
          height: '36px',
          border: 'none',
          background: 'none',
          color: '#E2E8F0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          borderRadius: '50%',
          transition: 'background-color 0.2s',
        }}
        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'}
        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
        title="Reset View"
      >
        <FaArrowRotateLeft size={13} />
      </button>

    </div>
  );
};

const CanvasBgColorPicker = ({ bgColor, setBgColor }) => {
  const colorInputRef = useRef(null);

  const handleColorChange = (e) => {
    const selectedColor = e.target.value;
    setBgColor(selectedColor.slice(0, 7));
  };

  return (
    <div className="bg-color-picker" style={{
      position: 'absolute',
      bottom: '24px',
      left: '24px',
      backgroundColor: 'rgba(23, 25, 30, 0.75)',
      backdropFilter: 'blur(8px)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      borderRadius: '20px',
      padding: '8px 14px',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      color: '#E2E8F0',
      fontFamily: 'Lato, sans-serif',
      fontSize: '13px',
      fontWeight: '600',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
      zIndex: 10,
      userSelect: 'none'
    }}>
      <span style={{ color: '#94A3B8' }}>Bg:</span>
      <div
        style={{
          position: 'relative',
          width: '20px',
          height: '20px',
          cursor: 'pointer',
          transition: 'transform 0.15s ease-in-out'
        }}
        onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
        onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1.0)'}
      >
        <div
          style={{
            width: '20px',
            height: '20px',
            borderRadius: '50%',
            backgroundColor: bgColor,
            border: '2px solid #FFFFFF',
            boxShadow: '0 0 4px rgba(0, 0, 0, 0.5)',
          }}
        />
        <input
          ref={colorInputRef}
          type="color"
          value={bgColor}
          onChange={handleColorChange}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            opacity: 0,
            cursor: 'pointer',
            border: 'none',
            padding: 0
          }}
        />
      </div>
      <span style={{ fontSize: '12px', fontFamily: 'monospace', letterSpacing: '0.5px' }}>
        {bgColor.toUpperCase()}
      </span>
    </div>
  );
};

const Model = () => {

  const { formData, loadingSet, objdata, setObjdata, setShowall, showall, setPartname, setShadowVisible, setInletuuids, setOutletuuids, setHighlightingText } = useGlobalcontext()
  const controlsRef = useRef(null);

  const [bgColor, setBgColor] = useState(() => {
    return localStorage.getItem('envBgColor') || '#e0f3faff'.slice(0, 7);
  });

  useEffect(() => {
    localStorage.setItem('envBgColor', bgColor);
  }, [bgColor]);

  const { show, hideAll } = useContextMenu({
    id: CANVAS_ID,
  });

  const isCurrentlyLoading = loadingSet && loadingSet.size > 0;

  useEffect(() => {
    if (!isCurrentlyLoading && controlsRef.current) {
      const timer = setTimeout(() => {
        if (controlsRef.current) {
          controlsRef.current.saveState();
        }
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isCurrentlyLoading]);


  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <Canvas
        style={{
          position: "relative",
          // background: "radial-gradient(circle, #2E3340 0%, #15181F 100%)"
        }}
        onContextMenu={(event) => {


          show({
            event,
            props: {
              key: 'value'
            }
          })
        }}>

        {/* Three.js Color doesn't support 8-digit hex strings (with alpha), so we slice to 7 chars (e.g. #a9a4af) */}
        <color attach="background" args={[bgColor]} />
        {/* Studio Key Light (Top-Front-Left) */}
        <directionalLight
          position={[-100, 150, 100]}
          intensity={1.2}
          shadow-mapSize={2048}
        />
        {/* Studio Fill Light (Top-Front-Right) */}
        <directionalLight
          position={[100, 120, 100]}
          intensity={0.6}
          shadow-mapSize={1024}
        />
        {/* Studio Rim Light (Top-Back) to separate model from background */}
        <directionalLight
          position={[0, 100, -100]}
          intensity={0.8}
          shadow-mapSize={1024}
        />
        {/* Soft ground fill light */}
        <directionalLight
          position={[0, -100, 0]}
          intensity={0.3}
        />
        <ambientLight intensity={0.4} />
        {/* F4F1F1 */}
        {/* D3D3D3 */}
        {/* e6f2ff */}
        {/* E9EEFC */}
        {/* EFE8FC */}
        {/* EEEDF8 */}
        {/* F5F0F1 */}
        <OrbitControls ref={controlsRef} enableZoom={true} enablePan={true} minPolarAngle={0} maxPolarAngle={Math.PI / 2.25} makeDefault minDistance={1.5} maxDistance={8} />
        <Model3d />
        {/* <axesHelper args={[2000]}/> */}
      </Canvas>
      <Menu id={CANVAS_ID} animation={false} theme='light' style={{ minWidth: "130px", padding: "5px" }}>
        {
          objdata.length > 0 && <>
            <p className='item remove-hover' onClick={() => {
              objdata.forEach((item) => {
                item.visible = false
              })
              setObjdata([])
              setOutletuuids([])
              setInletuuids([])
              setShadowVisible(false)
            }}>Hide</p>
            <Separator />
          </>
        }
        <p className='item remove-hover' onClick={() => {
          setShowall(!showall)
          setShadowVisible(true)
          setPartname("")
          setObjdata([])
          setOutletuuids([])
          setInletuuids([])
          setHighlightingText('')
        }}>Show all</p>
        {
          objdata.length > 0 && <>
            <Separator />
            <p className='item remove-hover' onClick={() => {
              objdata.forEach((item) => {
                item.traverse((node) => {
                  if (node.isMesh) {
                    if (node.material) {
                      if (Array.isArray(node.material)) {
                        node.material.forEach((mat) => mat.dispose());
                      } else {
                        node.material.dispose();
                      }
                    }
                    const material = new THREE.MeshStandardMaterial({
                      roughness: 0.3,
                      metalness: 0.4,
                      transparent: true,
                      opacity: 0.4,
                      depthWrite: false,
                    });
                    node.material = material;
                  }
                });
              })
              setObjdata([])
              setOutletuuids([])
              setInletuuids([])
              setShadowVisible(false)

            }}>Transparent</p>
          </>
        }
      </Menu>
      <ConfigurationStatusBar />
      <ViewerControls controlsRef={controlsRef} />
      <CanvasBgColorPicker bgColor={bgColor} setBgColor={setBgColor} />
    </div>

  )
}

export default Model




const PlaceholderBox = ({ length }) => {
  let minZ = -620;
  let maxZ = 2580;
  if (length === 2000) {
    minZ = -620;
    maxZ = 1580;
  } else if (length === 3000) {
    minZ = -620;
    maxZ = 2580;
  } else if (length === 4000) {
    minZ = -2620;
    maxZ = 1580;
  } else {
    minZ = -(length - 2400) - 20;
    maxZ = 2580;
  }
  const centerZ = (minZ + maxZ) / 2;
  const sizeZ = maxZ - minZ;
  return (
    <mesh name="placeholder-box" position={[0, 100, centerZ]}>
      <boxGeometry args={[400, 600, sizeZ]} />
      <meshBasicMaterial transparent opacity={0} depthWrite={false} />
    </mesh>
  );
};

const Model3d = () => {
  const { formData, showall, shadowvisible, loadingSet } = useGlobalcontext()

  var shadowheight = formData.diameter == 200 ? 0 : -80
  const isCurrentlyLoading = loadingSet && loadingSet.size > 0

  // Calculate sizeZ based on length
  const length = formData.length
  let minZ = -620;
  let maxZ = 2580;
  if (length === 2000) {
    minZ = -620;
    maxZ = 1580;
  } else if (length === 3000) {
    minZ = -620;
    maxZ = 2580;
  } else if (length === 4000) {
    minZ = -2620;
    maxZ = 1580;
  } else {
    minZ = -(length - 2400) - 20;
    maxZ = 2580;
  }
  const sizeZ = maxZ - minZ;

  const shadowWidth = sizeZ + 1200;
  const shadowDepth = formData.diameter === 200 ? 850 : 1000;

  return (
    <>
      {isCurrentlyLoading && <LoadingIndicator />}
      <Suspense key={`${showall}-${formData.length}`} fallback={<LoadingIndicator />}>
        <Stage adjustCamera={0.75} shadows={{ type: "contact", position: [0, shadowheight, 0], opacity: shadowvisible ? 2 : 0, scale: 25000 }}>
          <>
            <mesh rotation={[0, -Math.PI / 2, 0]}>
              <PlaceholderBox length={formData.length} />
              {/* Screw Length-2000 */}

              {
                formData.length === 2000 && <Length2000 />
              }

              {/* Screw Length-3000 */}

              {
                formData.length === 3000 && <Length3000 />
              }

              {/* Screw Length-4000 */}


              {
                formData.length === 4000 && <Length4000 />
              }

              {/* Screw Length-5000 */}
              {
                formData.length === 5000 && <Length5000 />
              }

              {/* Screw Length-6000 */}

              {
                formData.length === 6000 && <Length6000 />
              }

              {/* Screw Length-7000 */}

              {
                formData.length === 7000 && <Length7000 />
              }


              {/* Screw Length-8000 */}



              {
                formData.length === 8000 && <Length8000 />
              }

              {/* Screw Length-9000 */}

              {
                formData.length === 9000 && <Length9000 />
              }

              {/* Screw Length-10000 */}

              {
                formData.length === 10000 && <Length10000 />
              }

              {/* Screw Length-11000 */}

              {
                formData.length === 11000 && <Length11000 />
              }

              {/* Screw Length-12000 */}

              {
                formData.length === 12000 && <Length12000 />
              }
            </mesh>


          </>
          {/* </Selection> */}

        </Stage>

      </Suspense>
    </>
  )


}